<script setup>
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  tone: {
    type: String,
    default: 'info',
    validator: value => ['info', 'warning', 'error', 'success'].includes(value),
  },
  actionLabel: { type: String, default: '' },
  actionIcon: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
});

defineEmits(['action']);

const presentation = computed(() => {
  const tones = {
    info: {
      icon: 'i-lucide-info',
      container: 'border-n-blue-5 bg-n-blue-3/40',
      iconClass: 'text-n-blue-11',
    },
    warning: {
      icon: 'i-lucide-triangle-alert',
      container: 'border-n-amber-5 bg-n-amber-3/40',
      iconClass: 'text-n-amber-11',
    },
    error: {
      icon: 'i-lucide-circle-alert',
      container: 'border-n-ruby-5 bg-n-ruby-3/40',
      iconClass: 'text-n-ruby-11',
    },
    success: {
      icon: 'i-lucide-circle-check',
      container: 'border-n-teal-5 bg-n-teal-3/40',
      iconClass: 'text-n-teal-11',
    },
  };

  return tones[props.tone];
});
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between"
    :class="presentation.container"
    :role="tone === 'error' ? 'alert' : 'status'"
    :aria-live="tone === 'error' ? 'assertive' : 'polite'"
  >
    <div class="flex min-w-0 items-start gap-3">
      <span
        class="mt-0.5 size-5 shrink-0"
        :class="[presentation.icon, presentation.iconClass]"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="text-sm font-medium text-n-slate-12">{{ title }}</p>
        <p
          v-if="description"
          class="mt-1 text-xs leading-relaxed text-n-slate-11"
        >
          {{ description }}
        </p>
      </div>
    </div>
    <Button
      v-if="actionLabel"
      type="button"
      color="slate"
      variant="faded"
      size="sm"
      class="shrink-0"
      :icon="actionIcon"
      :label="actionLabel"
      :is-loading="isLoading"
      @click="$emit('action')"
    />
  </div>
</template>
